import * as THREE from "three";
import { Suspense, useEffect, useRef, useState } from "react";
import { extend, useFrame } from "@react-three/fiber";
import {
  CameraControls as DreiCameraControls,
  ContactShadows,
} from "@react-three/drei";
import CameraControlsImpl from "camera-controls";
import { geometry } from "maath";
import Frame, { frames } from "./Frame.jsx";
import CustomGltf from "./CustomGltf.jsx";
extend(geometry);

CameraControlsImpl.install({ THREE });

function FramePlaceholder({ position, width, height }) {
  const materialRef = useRef();
  useFrame((state) => {
    if (materialRef.current) {
      // Pulse opacity between 0.3 and 0.7 for skeleton effect
      materialRef.current.opacity = 0.5 + Math.sin(state.clock.getElapsedTime() * 4) * 0.2;
    }
  });

  return (
    <group position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh position={[0.5, 0, 0]}>
        <roundedPlaneGeometry args={[width, height, 0.1]} />
        <meshStandardMaterial
          ref={materialRef}
          color="#dcdcdc"
          transparent
          roughness={0.5}
        />
      </mesh>
    </group>
  );
}

export default function Experience() {
  let zOffset = 0;
  return (
    <>
      {frames.map((frame, i) => {
        const height = 0.6 + frame.months / 20; // your dynamic height logic
        const position = [0, 0, zOffset + height / 2]; // use the current offset
        zOffset += height + 0.2; // increment for the next frame

        return (
          <Suspense
            key={frame.id}
            fallback={
              <FramePlaceholder
                position={position}
                width={frame.width}
                height={height}
              />
            }
          >
            <Frame
              id={frame.id}
              jTitle={frame.jTitle}
              company_name={frame.company_name}
              job_description={frame.job_description}
              bg={frame.bg}
              position={position}
              width={frame.width}
              height={height}
              date={frame.date}
              company_website={frame.company_website}
              company_img={frame.company_img}
            >
              <Suspense fallback={null}>
                <CustomGltf
                  src={frame.model.src}
                  scale={frame.model.scale}
                  position={frame.model.position}
                  rotation={frame.model.rotation}
                />
              </Suspense>
            </Frame>
          </Suspense>
        );
      })}

      <Rig />

      <ContactShadows
        position={[0, -0.06, 0]} // just below frame Y=0
        opacity={0.5}
        scale={40}
        blur={1}
        far={10}
        resolution={512}
        color="#000000"
      />
    </>
  );
}
function Rig({
  position = new THREE.Vector3(0, 10, 0),
  focus = new THREE.Vector3(0, 0, 5),
}) {
  const controlsRef = useRef();
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const isMobile = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isTablet = screenSize.width >= 768 && screenSize.width < 1024;

  const getCameraSettings = () => {
    if (isMobile && screenSize.width < 768) {
      return {
        position: [0, 9, 5], // Further back and higher for mobile
        focus: [0, 0, 5],
        minDistance: 3,
        maxDistance: 25,
      };
    } else if (isTablet) {
      return {
        position: [0, 8, 6], // Medium distance for tablets
        focus: [0, 0, 5],
        minDistance: 2.5,
        maxDistance: 22,
      };
    } else {
      return {
        position: [0, 8, 5], // Original desktop position
        focus: [0, 0, 5],
        minDistance: 2,
        maxDistance: 20,
      };
    }
  };
  const dollySpeed = isMobile ? 5.0 : 1.5;
  const cameraSettings = getCameraSettings();

  useEffect(() => {
    if (controlsRef.current) {
      // Use responsive camera settings
      const { position: pos, focus: foc } = cameraSettings;
      position.set(...pos);
      focus.set(...foc);
      controlsRef.current.setLookAt(
        ...position.toArray(),
        ...focus.toArray(),
        false,
      );
    }
  }, [position, focus, cameraSettings]);
  console.log(cameraSettings);
  return (
    <DreiCameraControls
      ref={controlsRef}
      makeDefault
      dampingFactor={0.25}
      smoothTime={0.5}
      dragToRotate={false}
      enabledPan={true}
      enabledRotate={false}
      enabledZoom={true}
      minPolarAngle={0}
      maxPolarAngle={0}
      minAzimuthAngle={0}
      maxAzimuthAngle={0}
      minDistance={cameraSettings.minDistance}
      maxDistance={cameraSettings.maxDistance}
      dollySpeed={dollySpeed}
      mouseButtons={{
        left: CameraControlsImpl.ACTION.TRUCK, // Disable left click
        middle: CameraControlsImpl.ACTION.NONE, // Disable middle click
        right: CameraControlsImpl.ACTION.TRUCK, // Disable right click
        wheel: CameraControlsImpl.ACTION.ZOOM, // Enable scroll zoom
      }}
      // Touch controls for mobile
      touches={{
        one: CameraControlsImpl.ACTION.TOUCH_TRUCK,
        two: CameraControlsImpl.ACTION.TOUCH_ZOOM,
        three: CameraControlsImpl.ACTION.NONE,
      }}
    />
  );
}
